import { Button, Dialog, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import type { Organization } from '@eppendorf/vnls-user-tenant-utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getMigrationStatus, useMigrateTelemetry } from '$features/organizations';

export function MigrateTelemetryActionButton({ organization, migrationReport }: { organization: Organization; migrationReport?: string | null }) {
  const { t } = useTranslation();
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const { data: status, isLoading: isStatusLoading, refetch: refetchStatus } = getMigrationStatus(organization.id);
  const { mutateAsync: migrateTelemetry, isPending, error, isError, isSuccess } = useMigrateTelemetry();

  const isMigrating = isPending || status?.isMigratingTelemetry;
  const isMigrationDone = !!migrationReport?.includes('[TELEMETRY_MIGRATION_SUCCESS_MARKER]');

  const getButtonText = () => {
    if (isMigrating) {
      return t('organizationDetails.migrating');
    }
    if (isMigrationDone) {
      return t('organizationDetails.telemetryMigrated');
    }
    return t('organizationDetails.migrateTelemetry');
  };

  useEffect(() => {
    if (!isSuccess && !isError) {
      return;
    }

    if (isSuccess) {
      refetchStatus();
    }

    // @ts-expect-error - error is defined in the response
    const message = isError ? error.response?.data?.message || error?.message : 'Migration started!';
    toast(message, { type: isError ? 'error' : 'success' });
  }, [isSuccess, isError, error, refetchStatus]);

  return (
    <>
      <Button onClick={() => setOpenConfirmationDialog(true)} disabled={isMigrating || isMigrationDone || isStatusLoading}>
        <Icon
          name={isMigrationDone ? 'success' : 'import'}
          size={IconSizeClasses.XSmall}
          className={`m-right-xs ${isMigrating || isMigrationDone ? 'bg-gray-400' : 'bg-white'}`}
        />
        {getButtonText()}
      </Button>

      <Dialog isOpen={openConfirmationDialog} hideCloseButton>
        <div className="flex flex__dir--column flex__ai--center">
          <Icon name="warning" className="bg-orange-500 m-bottom-xxl" size={IconSizeClasses.XLarge} />

          <div className="title">{t('attentionConfirmation.title')}</div>

          <div className="body-regular">{t('attentionConfirmation.message')}</div>
        </div>

        <Dialog.Actions>
          <Button onClick={() => setOpenConfirmationDialog(false)} variant="secondary">
            {t('attentionConfirmation.cancel')}
          </Button>
          <Button
            onClick={() => {
              setOpenConfirmationDialog(false);
              migrateTelemetry(organization.legacyTenantId || '');
            }}
            variant="primary"
          >
            {t('attentionConfirmation.confirm')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
}
